"use strict";
/**
 * Withdrawal-stage projection and the backward-recursion
 * required-corpus solver.
 *
 * Retirement month numbering: month 1 is the FIRST month of
 * retirement (age = retirementAge exactly at start of month 1).
 *
 * Monthly cash-flow order (BEGINNING-of-month withdrawal):
 *   1. Compute openingBalance for month.
 *   2. Compute lifestyleExpense_m and dependableIncome_m in
 *      today's-rupees × inflation/escalation applied YEARS forward.
 *   3. portfolioWithdrawal = max(0, lifestyleExpense − dependableIncome)
 *      Excess income is NOT reinvested (documented conservatism).
 *   4. balanceAfterWithdrawal = openingBalance − portfolioWithdrawal
 *   5. endingBalance = balanceAfterWithdrawal × (1 + monthlyRate)
 *
 * Inflation of expense at retirement start:
 *   firstMonthlyLifestyleExpense =
 *     desiredRetirementLifestyleToday × (1 + preInflation) ^ (yearsToRetirement)
 *
 * Subsequent months escalate annually:
 *   lifestyle_year_k = firstMonthlyLifestyleExpense × (1 + postInflation) ^ k
 *   where k = floor((m-1) / 12).
 *
 * Dependable income:
 *   income_year_k = monthlyIncomeAtStart × (1 + escalation) ^ k
 *   income is only paid for months where incomeBeginsAtAge <= age < incomeEndsAtAge.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.initialAnnualisedWithdrawalIndicator = exports.projectWithdrawal = exports.calculateRequiredCorpus = exports.firstMonthlyLifestyleExpense = void 0;
const rates_1 = require("./rates");
function withdrawalMonths(inputs) {
    return (Math.max(0, inputs.planIncomeUntilAge - inputs.retirementAge) * 12);
}
function yearsToRetirement(inputs) {
    return Math.max(0, inputs.retirementAge - inputs.currentAge);
}
/**
 * First-month lifestyle expense at retirement, in future rupees.
 * Uses pre-retirement inflation.
 */
function firstMonthlyLifestyleExpense(inputs) {
    const years = yearsToRetirement(inputs);
    return ((inputs.desiredRetirementLifestyleToday || 0) *
        Math.pow(1 + (inputs.preRetirementInflation || 0), years));
}
exports.firstMonthlyLifestyleExpense = firstMonthlyLifestyleExpense;
function lifestyleExpenseForMonth(m, first, postInfl) {
    // Source-locked v1.1: continuous monthly escalation, NOT annual floor.
    // elapsedYears = (m - 1) / 12; month 1 uses exponent 0.
    const elapsedYears = (m - 1) / 12;
    return first * Math.pow(1 + postInfl, elapsedYears);
}
function incomeForMonth(m, inputs) {
    const currentAge = inputs.retirementAge + (m - 1) / 12;
    const beginsAt = inputs.incomeBeginsAtAge ?? inputs.retirementAge;
    const endsAt = inputs.incomeEndsAtAge ?? inputs.planIncomeUntilAge;
    if (currentAge + 1e-9 < beginsAt || currentAge >= endsAt)
        return 0;
    // Source-locked v1.1: continuous monthly escalation from income start.
    const yearsIntoIncome = Math.max(0, currentAge - beginsAt);
    return ((inputs.monthlyDependableIncomeAtStart || 0) *
        Math.pow(1 + (inputs.annualIncomeEscalation || 0), yearsIntoIncome));
}
function investableWithdrawalCorpus(inputs, projectedRetirementCorpus) {
    return Math.max(0, projectedRetirementCorpus -
        (inputs.healthcareEmergencyReserveAtRetirement || 0) -
        (inputs.oneTimeExpenseAtRetirement || 0));
}
/**
 * Backward recursion: what present value at retirement start is
 * needed to fund the withdrawal cash-flow stream, plus a terminal
 * corpus at plan end?
 *
 * Because withdrawals are beginning-of-month, we invert the forward
 * loop:
 *
 *   endingBalance    = balanceAfterWithdrawal × (1 + r)
 *   ⇒ balanceAfterWithdrawal = endingBalance / (1 + r)
 *   openingBalance   = balanceAfterWithdrawal + portfolioWithdrawal
 *
 * Starting from endingBalance at plan horizon =
 * desiredCorpusRemainingAtPlanEnd, we walk months backwards to
 * month 1. openingBalance at month 1 is the required WITHDRAWAL
 * corpus. The required TOTAL retirement corpus adds the healthcare
 * reserve and one-time expense.
 */
function calculateRequiredCorpus(inputs) {
    const totalMonths = withdrawalMonths(inputs);
    const monthlyRate = (0, rates_1.effectiveAnnualToMonthly)(inputs.withdrawalReturn);
    const first = firstMonthlyLifestyleExpense(inputs);
    let need = inputs.desiredCorpusRemainingAtPlanEnd || 0;
    for (let m = totalMonths; m >= 1; m--) {
        const lifestyle = lifestyleExpenseForMonth(m, first, inputs.postRetirementInflation);
        const income = incomeForMonth(m, inputs);
        const netWithdrawal = Math.max(0, lifestyle - income);
        const balanceAfterWithdrawal = need / (1 + monthlyRate);
        need = balanceAfterWithdrawal + netWithdrawal;
    }
    const requiredWithdrawalCorpus = Math.max(0, need);
    const firstIncome = incomeForMonth(1, inputs);
    const firstNet = Math.max(0, first - firstIncome);
    return {
        monthlyRate,
        requiredWithdrawalCorpus,
        requiredTotalRetirementCorpus: requiredWithdrawalCorpus +
            (inputs.healthcareEmergencyReserveAtRetirement || 0) +
            (inputs.oneTimeExpenseAtRetirement || 0),
        firstMonthlyLifestyleExpense: first,
        firstMonthlyDependableIncome: firstIncome,
        firstMonthlyNetPortfolioWithdrawal: firstNet,
    };
}
exports.calculateRequiredCorpus = calculateRequiredCorpus;
/**
 * Forward withdrawal projection from the actual projected corpus.
 * Used for the sustainability chart and the yearly schedule.
 */
function projectWithdrawal(inputs, projectedRetirementCorpus) {
    const totalMonths = withdrawalMonths(inputs);
    const monthlyRate = (0, rates_1.effectiveAnnualToMonthly)(inputs.withdrawalReturn);
    const first = firstMonthlyLifestyleExpense(inputs);
    const months = [];
    const years = [];
    const investable = investableWithdrawalCorpus(inputs, projectedRetirementCorpus);
    let balance = investable;
    let exhaustionAge = null;
    let yearExpense = 0;
    let yearIncome = 0;
    let yearPortfolioWithdrawal = 0;
    for (let m = 1; m <= totalMonths; m++) {
        const opening = balance;
        const lifestyle = lifestyleExpenseForMonth(m, first, inputs.postRetirementInflation);
        const income = incomeForMonth(m, inputs);
        const netWanted = Math.max(0, lifestyle - income);
        const actualWithdrawal = Math.min(netWanted, Math.max(0, opening));
        if (exhaustionAge === null && actualWithdrawal < netWanted - 1e-6) {
            exhaustionAge = inputs.retirementAge + (m - 1) / 12;
        }
        const afterWithdrawal = Math.max(0, opening - actualWithdrawal);
        const ending = afterWithdrawal * (1 + monthlyRate);
        months.push({
            monthIndex: m,
            age: inputs.retirementAge + m / 12,
            openingBalance: opening,
            lifestyleExpense: lifestyle,
            dependableIncome: income,
            portfolioWithdrawal: actualWithdrawal,
            balanceAfterWithdrawal: afterWithdrawal,
            endingBalance: ending,
        });
        balance = ending;
        yearExpense += lifestyle;
        yearIncome += income;
        yearPortfolioWithdrawal += actualWithdrawal;
        if (m % 12 === 0) {
            years.push({
                yearIndex: m / 12,
                ageAtEnd: inputs.retirementAge + m / 12,
                totalExpense: yearExpense,
                totalIncome: yearIncome,
                totalPortfolioWithdrawal: yearPortfolioWithdrawal,
                endingBalance: ending,
            });
            yearExpense = 0;
            yearIncome = 0;
            yearPortfolioWithdrawal = 0;
        }
    }
    const firstIncome = incomeForMonth(1, inputs);
    return {
        monthlyRate,
        months,
        years,
        investableWithdrawalCorpus: investable,
        firstMonthlyLifestyleExpense: first,
        firstMonthlyDependableIncome: firstIncome,
        firstMonthlyNetPortfolioWithdrawal: Math.max(0, first - firstIncome),
        endingBalanceAtPlanHorizon: balance,
        exhaustionAge,
    };
}
exports.projectWithdrawal = projectWithdrawal;
/**
 * Simple headline indicator: first-year net portfolio withdrawal
 * divided by investable withdrawal corpus. Purely informational —
 * NOT the SWP rule. Zero when investable corpus is zero.
 */
function initialAnnualisedWithdrawalIndicator(inputs, projectedRetirementCorpus, first, firstIncome) {
    const investable = investableWithdrawalCorpus(inputs, projectedRetirementCorpus);
    if (investable <= 0)
        return 0;
    const firstYearNet = Math.max(0, first - firstIncome) * 12;
    return firstYearNet / investable;
}
exports.initialAnnualisedWithdrawalIndicator = initialAnnualisedWithdrawalIndicator;
